import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Divider } from 'antd';
import {
    ArrowLeftOutlined,
    DatabaseFilled,
    GlobalOutlined,
    MessageOutlined,
    PhoneOutlined,
    ShoppingCartOutlined,
} from '@ant-design/icons';
import { Catcher, LoadingApiByIds } from '../';
import cx from 'classnames';
import { IGetApiType } from '../../REST';
import { ITariffPackagesToBuyItem, NotificationType } from '../../interfaces';
import { useProfile, useProfileIsRight, useRouterStore, useTariffsPackagesToBuyItem } from '../../hooks';
import {
    UnitTypeValues,
    abortSelectedFetch,
    addNotification,
    cleanTariffsPackagesToBuyItemStore,
    getCompaniesSelect,
    getCountriesSelect,
    getOperatorsSelect,
    getTariffPackagesToBuyItem,
} from '../../middleware';
import { showDataWithUnit, showOriginalUnitByType, stringMoney } from '../../instruments';
import { ITrField, tB, tF } from '../../translate';
import { AssignTariffModal } from './Components/AssignTariffModal/AssignTatiffModal';
import CardStyles from '../styles/m_itemCardStyles.less';
import TariffsStyles from './m_styles.less';

const TariffsPackagesItemBox = ({ Icon, title, value }: { Icon?: React.FC; title: ITrField; value?: string }) => {
    const rawValue = value?.split(' ') || ['0'];

    if (rawValue[0] === '0') {
        return null;
    }

    return (
        <div className={TariffsStyles.tariffsPackagesItemBox}>
            <div className={TariffsStyles.title}>{tF(title)}</div>
            <div className={TariffsStyles.value}>
                {Icon && <Icon />}
                {value}
            </div>
        </div>
    );
};

export const TariffPackagesToBuyItem: React.FC = observer(() => {
    const idSpinners: IGetApiType[] = ['GET_TARIFF_PACKAGE_TO_BUY'];
    const companyId = useProfile().companyId;
    const packageItem: ITariffPackagesToBuyItem = useTariffsPackagesToBuyItem();
    const routerStore = useRouterStore();
    const [isAssignOpen, setIsAssignOpen] = useState(false);
    const [id, setId] = useState('');
    const currentRouteId = routerStore.routerState.params.id;
    const isLord = useProfileIsRight('LORD');

    const {
        name,
        dataVolume,
        dataVolumeUnit,
        smsVolume,
        voiceVolume = 0,
        operators,
        networks,
        price,
        companies,
    } = packageItem;

    const goOut = (): void => {
        abortSelectedFetch(idSpinners);
        cleanTariffsPackagesToBuyItemStore();
    };

    const goTo = (event: React.MouseEvent): void => {
        if (!(event.ctrlKey || event.metaKey || event.altKey || event.shiftKey)) {
            event.preventDefault();
            goOut();
            routerStore.goTo('TariffPackagesToBuyList').then();
        }
    };

    const _getById = async (id: string): Promise<void> => {
        const isGet = await getTariffPackagesToBuyItem(id);
        if (!isGet) {
            addNotification({
                type: NotificationType.ERROR,
                message: 'Not Found',
                description: `Package - "${id}" not found`,
                duration: 5,
            });
            // goToList();
        }
    };

    useEffect(() => {
        return (): void => {
            goOut();
        };
    }, []);

    useEffect(() => {
        if (id) {
            _getById(id);
        }
    }, [id]);

    useEffect(() => {
        if (currentRouteId) {
            setId(currentRouteId);
            getOperatorsSelect().then();
            getCountriesSelect().then();
            if (isLord) {
                getCompaniesSelect().then();
            }
        }
    }, [currentRouteId, companyId]);

    const openAssignStepper = () => {
        setIsAssignOpen(true);
    };

    const closeAssignStepper = () => {
        setIsAssignOpen(false);
    };

    const companyInfo = packageItem?.companyId as never as { id: string; name: string };

    return (
        <Catcher>
            <div className={cx(CardStyles.main, TariffsStyles.container)}>
                <div className={TariffsStyles.tariffsPackagesItem}>
                    <Button onClick={(e) => goTo(e)} type={'ghost'}>
                        <ArrowLeftOutlined /> {tB('Back To Packages')}
                    </Button>

                    <div className={TariffsStyles.tariffsPackagesToBuyItemBox}>
                        <div className={TariffsStyles.title}>
                            <h1>{name}</h1>
                        </div>
                        <div className={TariffsStyles.buttonsContainer}>
                            <Button type={'primary'} onClick={openAssignStepper}>
                                <ShoppingCartOutlined /> {tB('Buy Package')}
                            </Button>
                        </div>
                    </div>
                    <Divider />
                    <div className={TariffsStyles.itemBoxesContainer}>
                        <TariffsPackagesItemBox
                            Icon={DatabaseFilled}
                            title={'Company'}
                            value={companyInfo?.name || 'No Company'}
                        />
                        <TariffsPackagesItemBox title={'Price'} value={stringMoney(price || 0, 'EUR')} />
                        {(packageItem.productId || packageItem.planType) && (
                            <>
                                {packageItem.productId && (
                                    <TariffsPackagesItemBox title={'Product ID'} value={packageItem.productId} />
                                )}
                                {packageItem.planType && (
                                    <TariffsPackagesItemBox title={'Plan Type'} value={packageItem.planType} />
                                )}
                            </>
                        )}
                        <TariffsPackagesItemBox
                            Icon={DatabaseFilled}
                            title={'Data Allowance'}
                            value={showDataWithUnit(dataVolume, dataVolumeUnit)}
                        />
                        <TariffsPackagesItemBox
                            Icon={PhoneOutlined}
                            title={'Call Allowance'}
                            value={showOriginalUnitByType(`${voiceVolume}`, UnitTypeValues.VOICE)}
                        />
                        <TariffsPackagesItemBox
                            Icon={MessageOutlined}
                            title={'SMS Allowance'}
                            value={showOriginalUnitByType(`${smsVolume}`, UnitTypeValues.SMS)}
                        />
                        {/*<div className={TariffsStyles.tariffsPackagesItemBox}>*/}
                        {/*    <div style={{ fontWeight: 'bold', marginBottom: 10, fontSize: '14px' }}>*/}
                        {/*        Used By Tariffs*/}
                        {/*    </div>*/}
                        {/*    <div style={{ display: 'flex', gap: '10px', fontSize: '24px' }}>*/}
                        {/*        <CreditCardOutlined />*/}
                        {/*        {tariffs?.length || 0}*/}
                        {/*    </div>*/}
                        {/*</div>*/}
                    </div>
                    {isLord ? (
                        <div className={TariffsStyles.tariffsPackagesItemBox}>
                            <h3 className={TariffsStyles.header3}>
                                {' '}
                                <GlobalOutlined className={TariffsStyles.icon} />
                                {tF('Assigned Companies')}
                            </h3>
                            <div className={TariffsStyles.tariffsPackagesItemOperator}>
                                {companies?.map((company) => (
                                    <span className={TariffsStyles.tariffsPackagesItemOperatorItem} key={company?.id}>
                                        {company?.name}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ) : null}
                    <div className={TariffsStyles.tariffsPackagesItemBox}>
                        <h3 className={TariffsStyles.header3}>
                            <GlobalOutlined className={TariffsStyles.icon} />
                            {tF('Operator')}
                        </h3>
                        <div className={TariffsStyles.tariffsPackagesItemOperator}>
                            {operators?.map((operator) => (
                                <span className={TariffsStyles.tariffsPackagesItemOperatorItem} key={operator?.id}>
                                    {operator?.name}
                                </span>
                            ))}
                        </div>
                    </div>
                    <div className={TariffsStyles.tariffsPackagesItemBox}>
                        <h3 className={TariffsStyles.header3}>
                            {' '}
                            <GlobalOutlined className={TariffsStyles.icon} />
                            {tF('Networks')}
                        </h3>
                        <div className={TariffsStyles.tariffsPackagesItemOperator}>
                            {networks?.map((network) => (
                                <span className={TariffsStyles.tariffsPackagesItemOperatorItem} key={network.id}>
                                    {network.name}
                                </span>
                            ))}
                        </div>
                    </div>
                    <AssignTariffModal
                        isOpen={isAssignOpen}
                        closeStepper={closeAssignStepper}
                        tariffPackage={packageItem}
                    />
                    <LoadingApiByIds idSpinners={idSpinners} />
                </div>
            </div>
        </Catcher>
    );
});
